import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function WhatsNextDialog({ open, onOpenChange, currentProjectId, value, onSave }) {
  const v = value || {};
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projectId, setProjectId] = useState(v.project_id || "");
  const [heading, setHeading] = useState(v.heading || "What's Next");
  const [cta, setCta] = useState(v.cta || "");

  useEffect(() => {
    if (!open) return;
    setProjectId(v.project_id || ""); setHeading(v.heading || "What's Next"); setCta(v.cta || "");
    setLoading(true);
    api.get("/projects")
      .then(({ data }) => setProjects((data || []).filter((p) => p.id !== currentProjectId)))
      .catch(() => toast.error("Couldn't load your books"))
      .finally(() => setLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (enabled = true) => {
    if (enabled && !projectId) { toast.message("Pick a book to feature"); return; }
    setSaving(true);
    try {
      const chosen = projects.find((p) => p.id === projectId);
      await onSave({ enabled, project_id: projectId, title: chosen?.title || "", heading: heading.trim() || "What's Next", cta: cta.trim() });
      toast.success(enabled ? "\u201cWhat\u2019s Next\u201d teaser saved" : "\u201cWhat\u2019s Next\u201d removed");
      onOpenChange(false);
    } catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-md" data-testid="whatsnext-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> What's Next</DialogTitle>
          <DialogDescription>
            Tease another of your books at the very end. On export, we'll add its cover and first chapter as a sneak-peek — a great way to hook readers into your next story.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading your books…</div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>Feature this book</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="whatsnext-project-select"><SelectValue placeholder="Choose one of your books…" /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
                  {!projects.length && <div className="px-3 py-2 text-sm text-muted-foreground">You need another book to feature.</div>}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Its cover + first chapter are pulled in automatically at export time.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Section heading</Label>
              <Input value={heading} onChange={(e) => setHeading(e.target.value)} placeholder="What's Next" data-testid="whatsnext-heading" />
            </div>
            <div className="space-y-1.5">
              <Label>Call to action <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="Available now — search 'Title' on Amazon" data-testid="whatsnext-cta" />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {v.enabled && (
            <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="whatsnext-remove" className="gap-1.5 mr-auto text-destructive">
              <Trash2 className="w-4 h-4" /> Remove
            </Button>
          )}
          <Button onClick={() => save(true)} disabled={saving || loading} data-testid="whatsnext-save" className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Save teaser
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
